import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Clock, Trash2 } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const SearchBar = ({ 
  placeholder = "Search...", 
  onSearch, 
  className = "" 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [searchHistory, setSearchHistory] = useState([]);
  const searchBarRef = useRef(null);
  const { isDarkMode } = useTheme();

  // Load search history from localStorage on mount
  useEffect(() => {
    const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
    setSearchHistory(history);
  }, []);

  // Handle clicks outside to close history
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
        setShowHistory(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const saveToHistory = (term) => {
    if (!term.trim()) return;
    
    const history = JSON.parse(localStorage.getItem('searchHistory') || '[]');
    // Remove if already exists
    const filtered = history.filter(h => h.toLowerCase() !== term.toLowerCase());
    // Add to beginning and limit to 5
    const newHistory = [term, ...filtered].slice(0, 5);
    localStorage.setItem('searchHistory', JSON.stringify(newHistory));
    setSearchHistory(newHistory);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      saveToHistory(searchTerm);
    if (onSearch) {
      onSearch(searchTerm);
      }
      setShowHistory(false);
    } else {
      if (onSearch) {
        onSearch('');
      }
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (value) {
      setShowHistory(false);
    }
  };

  const handleInputFocus = () => {
    if (!searchTerm && searchHistory.length > 0) {
      setShowHistory(true);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (onSearch) {
      onSearch('');
    }
  };

  const handleHistoryClick = (term) => {
    setSearchTerm(term);
    saveToHistory(term);
    if (onSearch) {
      onSearch(term);
    }
    setShowHistory(false);
  };

  const clearHistory = (e) => {
    e.stopPropagation();
    localStorage.removeItem('searchHistory');
    setSearchHistory([]);
  };

  const deleteHistoryItem = (e, item) => {
    e.stopPropagation();
    const history = searchHistory.filter(h => h !== item);
    localStorage.setItem('searchHistory', JSON.stringify(history));
    setSearchHistory(history);
  };

  return (
    <div ref={searchBarRef} className={`relative ${className}`}>
      <form onSubmit={handleSearch}>
      <div className="relative">
        <Search size={20} className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
        <input
          type="text"
          value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
          placeholder={placeholder}
          className={`w-full pl-10 pr-10 py-3 rounded-lg border transition-all ${
            isDarkMode 
              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 focus:ring-2 focus:ring-gray-500' 
              : 'bg-gray-100 border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-white focus:ring-2 focus:ring-gray-400'
          } focus:outline-none`}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={clearSearch}
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 transition-colors ${
              isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <X size={16} />
          </button>
        )}
      </div>
    </form>

      {/* Search History Dropdown */}
      {showHistory && searchHistory.length > 0 && (
        <div className={`absolute top-full left-0 right-0 mt-2 rounded-lg border shadow-lg z-50 ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <Clock size={16} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
              <span className={`text-sm font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Recent Searches
              </span>
            </div>
            <button
              onClick={clearHistory}
              className={`text-xs hover:underline ${isDarkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Clear all
            </button>
          </div>
          <div className="max-h-60 overflow-y-auto">
            {searchHistory.map((item, index) => (
              <div
                key={index}
                onClick={() => handleHistoryClick(item)}
                className={`flex items-center justify-between px-4 py-2 cursor-pointer hover:bg-gray-100 transition-colors ${
                  isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <Search size={14} className={`flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                  <span className={`text-sm truncate ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {item}
                  </span>
                </div>
                <button
                  onClick={(e) => deleteHistoryItem(e, item)}
                  className={`ml-2 p-1 rounded hover:bg-gray-200 transition-colors flex-shrink-0 ${
                    isDarkMode ? 'hover:bg-gray-600' : ''
                  }`}
                >
                  <Trash2 size={14} className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;